import { Injectable } from "@angular/core";

/**
 * Result shape for a public registry lookup. Uses snake_case-free names so
 * callers can map straight into Firestore documents. All fields are optional;
 * `name` is the only one that's always present when `found` is true.
 */
export interface IdentityLookupResult {
  found: boolean;
  source?: "uca" | "hacienda";
  name?: string;
  lastname?: string;
  /** ISO yyyy-mm-dd if available. */
  birthdate?: string;
  /** 1 = persona física, 2 = persona jurídica. */
  personType?: 1 | 2;
  /** First active economic activity (Hacienda only). Useful for `category`. */
  category?: string;
  /** Tax status for entities. `"Inscrito"` / `"No inscrito"`. */
  taxStatus?: string;
  /** Birthplace (UCA only). Free-form. */
  birthplace?: string;
  /** Original error if everything failed. */
  error?: string;
}

const UCA_URL = "https://registrocivil.uca.ac.cr/api/consulta_cedula";
const HACIENDA_URL = "https://api.hacienda.go.cr/fe/ae";

/** Cached lookups expire after this many ms. Hacienda + UCA data is stable. */
const LOOKUP_TTL_MS = 24 * 60 * 60 * 1000;
const CACHE_KEY = "identity-lookup-cache-v1";

/**
 * Per-provider absolute timeouts. UCA is consistently fast; Hacienda is the
 * slow/flaky one, so we cap it harder. After the timeout the fetch is aborted
 * and the lookup returns whatever it has so far.
 */
const UCA_TIMEOUT_MS = 5_000;
const HACIENDA_TIMEOUT_MS = 8_000;
/**
 * When UCA already gave us name/lastname/birthdate (physical IDs only), we
 * only stick around this long for Hacienda to add `category`/`taxStatus`.
 * Past that, return what we have and let the user pick the category manually.
 */
const HACIENDA_GRACE_MS = 1_500;

interface CacheEntry {
  result: IdentityLookupResult;
  expiresAt: number;
}

/**
 * Looks up a Costa Rica national or corporate ID against the public registries.
 *
 * Strategy:
 *   1. Cache hit → return immediately. Cache lives in `sessionStorage` (per
 *      tab) plus an in-memory copy so subsequent lookups in the same flow
 *      don't even touch storage.
 *   2. If 9-digit national ID → try UCA (Registro Civil) for the citizen.
 *   3. Always try Hacienda DGT for tax + activity data.
 *   4. Merge the results, preferring UCA's name parsing for individuals and
 *      Hacienda's activity for the `category` field.
 *
 * Both endpoints expose `Access-Control-Allow-Origin: *`, so the browser can
 * call them directly. Errors are swallowed — a failed lookup just returns
 * `{ found: false }` so the modal falls back to manual entry.
 */
@Injectable({ providedIn: "root" })
export class IdentityLookupService {
  private memoryCache = new Map<string, CacheEntry>();

  async lookup(rawDocumentId: string): Promise<IdentityLookupResult> {
    const cedula = (rawDocumentId || "").replace(/\D/g, "");
    if (!cedula) return { found: false };

    const cached = this.readCached(cedula);
    if (cached) return cached;

    const isPhysical = cedula.length === 9;

    // Strategy: kick off both providers in parallel so Hacienda has as much
    // wall-clock time as possible to finish before we look at it. UCA almost
    // always wins. Once UCA resolves we only give Hacienda a short grace
    // window — its data is nice-to-have (category) and it tends to be slow.
    const ucaPromise = isPhysical
      ? this.fetchUca(cedula).catch(() => null)
      : Promise.resolve(null);
    const hacPromise = this.fetchHacienda(cedula).catch(() => null);

    let ucaData: Awaited<ReturnType<typeof this.fetchUca>> = null;
    let hacData: Awaited<ReturnType<typeof this.fetchHacienda>> = null;

    if (isPhysical) {
      ucaData = await ucaPromise;
      if (ucaData) {
        hacData = await Promise.race([
          hacPromise,
          new Promise<null>((resolve) =>
            setTimeout(() => resolve(null), HACIENDA_GRACE_MS)
          ),
        ]);
      } else {
        // UCA missed (e.g. cédula not in their DB) — fall back to Hacienda
        // alone. It already has its own absolute timeout.
        hacData = await hacPromise;
      }
    } else {
      // Corporate ID (10+ digits). Only Hacienda has data; just wait for it.
      hacData = await hacPromise;
    }

    if (!ucaData && !hacData) {
      const result: IdentityLookupResult = { found: false };
      // Cache the negative result for a much shorter window so the user can
      // retry after a registry outage.
      this.writeCached(cedula, result, 60 * 1000);
      return result;
    }

    const result: IdentityLookupResult = {
      found: true,
      source: ucaData ? "uca" : "hacienda",
      name: ucaData?.name ?? hacData?.name,
      lastname: ucaData?.lastname ?? "",
      birthdate: ucaData?.birthdate,
      birthplace: ucaData?.birthplace,
      personType: ucaData ? 1 : hacData?.personType ?? (isPhysical ? 1 : 2),
      category: hacData?.category,
      taxStatus: hacData?.taxStatus,
    };
    this.writeCached(cedula, result, LOOKUP_TTL_MS);
    return result;
  }

  private readCached(cedula: string): IdentityLookupResult | null {
    const inMemory = this.memoryCache.get(cedula);
    if (inMemory && inMemory.expiresAt > Date.now()) return inMemory.result;
    if (inMemory) this.memoryCache.delete(cedula);

    if (typeof window === "undefined" || !window.sessionStorage) return null;
    try {
      const raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const map = JSON.parse(raw) as Record<string, CacheEntry>;
      const entry = map[cedula];
      if (entry && entry.expiresAt > Date.now()) {
        this.memoryCache.set(cedula, entry);
        return entry.result;
      }
    } catch {
      // Cache unreadable — drop it and start over.
      try {
        window.sessionStorage.removeItem(CACHE_KEY);
      } catch { /* ignore */ }
    }
    return null;
  }

  private writeCached(cedula: string, result: IdentityLookupResult, ttlMs: number): void {
    const entry: CacheEntry = { result, expiresAt: Date.now() + ttlMs };
    this.memoryCache.set(cedula, entry);

    if (typeof window === "undefined" || !window.sessionStorage) return;
    try {
      const raw = window.sessionStorage.getItem(CACHE_KEY);
      const map = raw ? (JSON.parse(raw) as Record<string, CacheEntry>) : {};
      map[cedula] = entry;
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(map));
    } catch {
      // sessionStorage full or unavailable — in-memory cache still works.
    }
  }

  // ---- providers ----------------------------------------------------------

  private async fetchUca(cedula: string): Promise<{
    name: string;
    lastname: string;
    birthdate?: string;
    birthplace?: string;
  } | null> {
    try {
      const json = await this.fetchJsonWithEncodingFallback(
        `${UCA_URL}/${cedula}`,
        UCA_TIMEOUT_MS
      );
      if (!json?.ok || !json?.data) return null;
      const d = json.data;
      return {
        name: this.titleCase(d.nombre ?? ""),
        lastname: this.titleCase(
          [d.primer_apellido, d.segundo_apellido].filter(Boolean).join(" ")
        ),
        birthdate: this.toIsoDate(d.fecha_suceso),
        birthplace: this.titleCase(d.lugar_nacimiento ?? ""),
      };
    } catch {
      return null;
    }
  }

  private async fetchHacienda(cedula: string): Promise<{
    name: string;
    personType: 1 | 2;
    category?: string;
    taxStatus?: string;
  } | null> {
    try {
      const json = await this.fetchJsonWithEncodingFallback(
        `${HACIENDA_URL}?identificacion=${cedula}`,
        HACIENDA_TIMEOUT_MS
      );
      if (!json?.nombre) return null;
      const personType: 1 | 2 = json.tipoIdentificacion === "02" ? 2 : 1;
      const principal = (json.actividades || []).find((a: any) => a.tipo === "P")
        ?? (json.actividades || [])[0];
      return {
        name: this.titleCase(json.nombre),
        personType,
        category: principal?.descripcion,
        taxStatus: json.situacion?.estado,
      };
    } catch {
      return null;
    }
  }

  // ---- formatters ---------------------------------------------------------

  private toIsoDate(yyyymmdd: string | undefined): string | undefined {
    if (!yyyymmdd || yyyymmdd.length !== 8 || yyyymmdd === "00000000") return undefined;
    const y = yyyymmdd.slice(0, 4);
    const m = yyyymmdd.slice(4, 6);
    const d = yyyymmdd.slice(6, 8);
    return `${y}-${m}-${d}`;
  }

  /**
   * Some public endpoints occasionally return JSON bytes in latin1/cp1252 while
   * announcing UTF-8. Decode with UTF-8 first and fall back to latin1 when
   * replacement chars (�) indicate broken decoding.
   *
   * `timeoutMs` aborts the fetch if the server stalls past that limit, so a
   * slow public registry doesn't lock up the modal.
   */
  private async fetchJsonWithEncodingFallback(
    url: string,
    timeoutMs: number
  ): Promise<any | null> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    let res: Response;
    try {
      res = await fetch(url, { signal: ctrl.signal });
    } catch {
      // AbortError or network failure — caller treats as "no data".
      return null;
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) return null;

    const buffer = await res.arrayBuffer();
    const utf8Text = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
    const latinText = new TextDecoder("iso-8859-1", { fatal: false }).decode(buffer);

    const utf8Parsed = this.tryParseJson(utf8Text);
    const latinParsed = this.tryParseJson(latinText);

    if (utf8Parsed && !this.hasReplacementChar(utf8Text)) return utf8Parsed;
    if (latinParsed && this.hasReplacementChar(utf8Text)) return latinParsed;

    return utf8Parsed ?? latinParsed ?? null;
  }

  private tryParseJson(text: string): any | null {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  private hasReplacementChar(value: string): boolean {
    return value.includes("\uFFFD");
  }

  /**
   * Title-cases a string while preserving accents and Spanish particles
   * ("DE", "DEL", "LA"). Both endpoints return everything in upper-case.
   */
  private titleCase(s: string): string {
    if (!s) return "";
    const lowers = new Set(["de", "del", "la", "las", "los", "y", "en"]);
    return s
      .toLowerCase()
      .split(/\s+/)
      .map((word, i) =>
        lowers.has(word) && i > 0
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(" ")
      .trim();
  }
}
